// src/repositories/supabase/SupabaseEntityRepository.ts
import type { SupabaseClient } from '@supabase/supabase-js';
import type { IEntityRepository } from '../types';
import type { DBEntity } from '../../types/db';
import { normalizeForSearch } from '../../types/db';
import { requireAuth } from '../../services/supabaseClient';
import { encrypt, deriveKeyFromSession } from '../../services/EncryptionService';
import { DEFAULT_FIRM_ID } from '../../utils/assertFirmId';
import { ENTITY_COLS } from '../../constants/queryColumns';

function mapRowToDBEntity(row: Record<string, unknown>): DBEntity {
  return {
    dbId:             (row.id as number | null) ?? undefined,
    firmId:           row.firm_id as string,
    companyId:        row.company_id as string,
    tipoDocIdentidad: row.tipo_doc_identidad as string,
    numeroDoc:        row.numero_doc as string,
    razonSocial:      row.razon_social as string, // encrypted — never decrypt here
    razonSocialNorm:  row.razon_social_norm as string,
    tipo:             row.tipo as DBEntity['tipo'],
    cuentaPcge:       (row.cuenta_pcge as string | null) ?? undefined,
    nombreCuentaPcge: (row.nombre_cuenta_pcge as string | null) ?? undefined,
    createdAt:        row.created_at as string,
    updatedAt:        row.updated_at as string,
  };
}

export class SupabaseEntityRepository implements IEntityRepository {
  private readonly client: SupabaseClient;
  constructor(client: SupabaseClient) { this.client = client; }

  async getByDoc(
    firmId: string,
    companyId: string,
    numeroDoc: string,
  ): Promise<DBEntity | undefined> {
    // Guard: DEFAULT_FIRM_ID is not a real UUID — Postgres rejects it with "invalid input syntax"
    if (!firmId || firmId === DEFAULT_FIRM_ID) return undefined;
    await requireAuth();
    const { data, error } = await this.client
      .from('entities')
      .select(ENTITY_COLS)
      .eq('firm_id', firmId)
      .eq('company_id', companyId)
      .eq('numero_doc', numeroDoc)
      .neq('is_deleted', true)
      .limit(1);
    if (error) throw new Error(`[getByDoc] ${error.message}`);
    if (!data || data.length === 0) return undefined;
    return mapRowToDBEntity(data[0] as unknown as Record<string, unknown>);
  }

  async getByDocs(
    firmId: string,
    companyId: string,
    numeroDocs: string[],
  ): Promise<Map<string, DBEntity>> {
    const out = new Map<string, DBEntity>();
    // Same guard as getByDoc: DEFAULT_FIRM_ID is not a real UUID
    if (!firmId || firmId === DEFAULT_FIRM_ID || numeroDocs.length === 0) return out;
    await requireAuth();
    const { data, error } = await this.client
      .from('entities')
      .select(ENTITY_COLS)
      .eq('firm_id', firmId)
      .eq('company_id', companyId)
      .in('numero_doc', numeroDocs)
      .neq('is_deleted', true);
    if (error) throw new Error(`[getByDocs] ${error.message}`);
    for (const row of (data ?? []) as unknown as Record<string, unknown>[]) {
      const entity = mapRowToDBEntity(row);
      // Primera fila por numeroDoc gana — mismo criterio que getByDoc con limit(1)
      if (!out.has(entity.numeroDoc)) out.set(entity.numeroDoc, entity);
    }
    return out;
  }

  async upsert(entity: Omit<DBEntity, 'dbId' | 'razonSocialNorm'>): Promise<void> {
    await requireAuth();
    const norm        = normalizeForSearch(entity.razonSocial);
    const encKey      = await deriveKeyFromSession(entity.companyId);
    const encRazonSocial = await encrypt(entity.razonSocial, encKey);
    const { error } = await this.client
      .from('entities')
      .upsert([{
        firm_id:            entity.firmId,
        company_id:         entity.companyId,
        tipo_doc_identidad: entity.tipoDocIdentidad,
        numero_doc:         entity.numeroDoc,
        razon_social:       encRazonSocial,
        razon_social_norm:  norm,
        tipo:               entity.tipo,
        cuenta_pcge:        entity.cuentaPcge ?? null,
        nombre_cuenta_pcge: entity.nombreCuentaPcge ?? null,
        created_at:         entity.createdAt,
        updated_at:         new Date().toISOString(),
      }], { onConflict: 'firm_id,company_id,numero_doc' });
    if (error) throw new Error(`[upsert] ${error.message}`);
  }

  async getAll(firmId: string, companyId: string): Promise<DBEntity[]> {
    await requireAuth();
    const { data, error } = await this.client
      .from('entities')
      .select(ENTITY_COLS)
      .eq('firm_id', firmId)
      .eq('company_id', companyId)
      .neq('is_deleted', true)
      .limit(5000);
    if (error) throw new Error(`[getAll] ${error.message}`);
    return ((data ?? []) as unknown as Record<string, unknown>[]).map(mapRowToDBEntity);
  }

  async buildEntityMap(
    firmId: string,
    companyId: string,
  ): Promise<Map<string, DBEntity>> {
    const entities = await this.getAll(firmId, companyId);
    const map = new Map<string, DBEntity>();
    for (const e of entities) {
      map.set(e.razonSocialNorm, e);
    }
    return map;
  }

  async deleteById(firmId: string, companyId: string, id: number): Promise<void> {
    await requireAuth();
    // Soft delete — preserve entity history for audit trail (SA-1)
    const { error } = await this.client
      .from('entities')
      .update({ is_deleted: true })
      .eq('firm_id', firmId)
      .eq('company_id', companyId)
      .eq('id', id);
    if (error) throw new Error(`[deleteById] ${error.message}`);
  }
}
